import { ownerGateWaitingLine, type OwnerGateWaitingItem } from '../lib/owner-gate-waiting';

interface Props {
  /** Merged runs with a recorded owner gate that evidence has not resolved. */
  readonly ownerGates?: readonly OwnerGateWaitingItem[];
  readonly planCount: number;
  /** Older proposals whose work is finished or replaced. Opens the archive and is not an owner decision. */
  readonly archivedCount?: number;
  readonly onReviewPlans: () => void;
}

export function ManagementAttention({ ownerGates = [], planCount, archivedCount = 0, onReviewPlans }: Props) {
  const attentionCount = planCount + ownerGates.length;

  return (
    <section id="main-command-attention" className="main-command-attention stack" aria-label="Management attention">
      <header className="row spread">
        <div>
          <h2>Management attention</h2>
          <small className="muted">Plans and owner gates that wait on you.</small>
        </div>
        {attentionCount > 0 && <span className="count">{attentionCount}</span>}
      </header>

      {ownerGates.length > 0 && (
        <div className="management-attention-group" data-owner-gate-waiting>
          <h3>Waiting on you</h3>
          {ownerGates.map((item) => (
            <article key={item.id} className="management-attention-card urgency-attention" data-owner-gate={item.id}>
              <header className="row spread">
                <div>
                  <p className="management-attention-source">Merged run · Owner gate</p>
                  <h3>{ownerGateWaitingLine(item)}</h3>
                </div>
                <span className="tag">Owner gate</span>
              </header>
              <dl className="management-attention-facts">
                <div><dt>Pull request</dt><dd>{item.pullRequest}</dd></div>
                <div><dt>Gate</dt><dd>{item.gate}</dd></div>
                <div><dt>Target</dt><dd>{item.version === null ? item.target : `${item.target} · ${item.version}`}</dd></div>
              </dl>
            </article>
          ))}
          <small className="muted">Recorded and still outstanding. Nothing here applies a migration or rolls out a release.</small>
        </div>
      )}

      {planCount > 0 && (
        <article className="management-attention-card urgency-attention">
          <header className="row spread">
            <div>
              <p className="management-attention-source">Main Overseer · Plan decision</p>
              <h3>{planCount} plan{planCount === 1 ? '' : 's'} waiting for owner review</h3>
            </div>
            <span className="tag">Owner decision</span>
          </header>
          <p>Review the existing canonical plan proposals. Approval keeps its existing plan authority and does not create a second task path.</p>
          <button type="button" className="primary" onClick={onReviewPlans}>Review plans</button>
        </article>
      )}

      {archivedCount > 0 && (
        <article className="management-attention-card" data-proposal-archive="true">
          <header className="row spread">
            <div>
              <p className="management-attention-source">Main Overseer · Plan archive</p>
              <h3>Archive ({archivedCount})</h3>
            </div>
          </header>
          <p>Older proposals whose work is finished or replaced. The record stays here.</p>
          <button type="button" onClick={onReviewPlans}>Open archive</button>
        </article>
      )}

      {attentionCount === 0 && <p className="muted">Nothing needs an owner decision right now.</p>}
    </section>
  );
}
